import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { setShiftBoundaries, setShiftEditability, setShiftEditabilityDataLoading } from "../../state/reducers/shiftReducer";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useTimezone } from "../Providers/TimezoneProvider";
import { RootState } from "../../state/store";
import { shiftEnd, shiftStart } from "../../offline/shiftBands";
import { shiftNameToLetter } from "../../types/Shift";

export default function useShiftBoundarySync() {
  const dispatch = useDispatch();
  const { timezone, isTimezoneLoading } = useTimezone();
  const selectedDateStr = useSelector((state: RootState) => state.shift.selectedDate);
  const selectedShift = useSelector((state: RootState) => state.shift.selectedShift);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate);
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift);

  const isReady = !!selectedDateStr && !!selectedShift && !isTimezoneLoading && !!timezone;
  const selectedDate = selectedDateStr && timezone
    ? DateTime.fromISO(selectedDateStr).toLocal().setZone(timezone, { keepLocalTime: true }).startOf("day")
    : null;
  const backendCode = shiftNameToLetter(selectedShift);

  useEffect(() => {
    if (!selectedDateStr || !selectedShift) return;
    dispatch(setShiftBoundaries({
      start: shiftStart(selectedDateStr, selectedShift).toISO()!,
      end: shiftEnd(selectedDateStr, selectedShift).toISO()!
    }));
  }, [dispatch, selectedDateStr, selectedShift]);

  const {
    data: boundaries,
    error: boundariesError,
    isFetching: isBoundariesFetching,
  } = useQuery<{ shiftStart: DateTime; shiftEnd: DateTime }, Error>({
    queryKey: ['shift', 'boundaries', selectedDateStr, selectedShift, timezone],
    queryFn: async () => {
      try {
        const res = await settingsAjax.getShiftBoundaries(selectedDate!, backendCode);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages[0].description);
        return res;
      } catch (error) {
        console.error("Failed to fetch shift boundaries", error);
        throw error instanceof Error ? error : new Error("Failed to fetch shift boundaries");
      }
    },
    enabled: isReady,
  });

  const {
    data: editability,
    error: editabilityError,
    isFetching: isEditabilityFetching,
  } = useQuery<{ isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null }, Error>({
    queryKey: ['shift', 'editability', selectedDateStr, selectedShift, timezone],
    queryFn: async () => {
      try {
        const res = await settingsAjax.getShiftEditability(selectedDate!, backendCode);
        if (ErrorResponse.isErrorResponse(res)) throw new Error(res.messages[0].description);
        return res;
      } catch (error) {
        console.error("Failed to fetch shift editability", error);
        throw error instanceof Error ? error : new Error("Failed to fetch shift editability");
      }
    },
    enabled: isReady,
  });

  useEffect(() => {
    if (!boundaries) return;
    dispatch(setShiftBoundaries({ start: boundaries.shiftStart.toISO()!, end: boundaries.shiftEnd.toISO()! }));
  }, [boundaries, dispatch]);

  useEffect(() => {
    if (editability) {
      dispatch(setShiftEditability(editability));
      return;
    }
    if (editabilityError) {
      const isLatestShift = (latestShiftDate === selectedDateStr && latestShift === selectedShift)
        || (provisionalShiftDate === selectedDateStr && provisionalShift === selectedShift);
      dispatch(setShiftEditability({ isLocked: !isLatestShift, canToggleLock: false, isPastAutoLockCutOff: null }));
    }
  }, [editability, editabilityError, dispatch, latestShiftDate, latestShift, provisionalShiftDate, provisionalShift, selectedDateStr, selectedShift]);

  useEffect(() => {
    dispatch(setShiftEditabilityDataLoading(isReady && (isBoundariesFetching || isEditabilityFetching)));
  }, [isReady, isBoundariesFetching, isEditabilityFetching, dispatch]);

  return {
    isTimezoneLoading,
    shiftBoundaryFetchError: boundariesError?.message ?? null,
    shiftEditabilityFetchError: editabilityError?.message ?? null,
  };
}
